import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Tooltip } from '@cimpress/react-components';
import { auth } from '../../../utils/auth';
import { isProduction } from '../../../utilities/processFunctions';
import PricingGrid from '@cimpress-technology/pricing-grid-ts';
import UnionSelector from '@cimpress-technology/union-selector';
import cloneDeep from 'lodash/cloneDeep';
import { connect } from 'react-redux';
import { selectors as termSelectors } from '../../../reducers/terms';

class DiffPricingGrid extends Component {
  static defaultProps = {};

  static propTypes = {
    from: PropTypes.object,
    to: PropTypes.object,
    sku: PropTypes.string,
    fromPriceModelInfo: PropTypes.object,
    toPriceModelInfo: PropTypes.object,
    skuRuleSet: PropTypes.object
  };

  state = {};

  getGridData(termRevision, priceModelInfo, columnTitle) {
    return {
      preview: this.getPreview(termRevision, priceModelInfo, columnTitle),
      priceModelUrl:
        priceModelInfo &&
        `${process.env.REACT_APP_PRICINGMANAGER_URL}/models/${priceModelInfo.id}?revision=${priceModelInfo.revisionId}`,
      hasMultipleCurrencies: get(priceModelInfo, 'possibleCurrencies.length', 0) > 1
    };
  }

  getPreview(termRevision, priceModelInfo, columnTitle) {
    if (!termRevision || !priceModelInfo) {
      return null;
    }

    const discountPercent = get(termRevision, 'modifiers.discountPercent', null);
    const markupPercent = get(termRevision, 'modifiers.markupPercent', null);

    return {
      title: columnTitle || get(termRevision, 'displayAgreementType', '') || 'Contract Price',
      priceModelId: priceModelInfo.id,
      revisionId: priceModelInfo.revisionId,
      discountPercent,
      markupPercent
    };
  }
  onChange = productState => {
    const { sku } = this.props;
    let newProductState = {};

    if (productState[sku]) {
      newProductState[sku] = productState[sku];
    }
    const newProductStateValues = Object.values(newProductState);

    if (newProductStateValues && newProductStateValues.length > 0) {
      const { displayedAttributes } = newProductStateValues[0];
      this.setState({
        productState: newProductState,
        initialDisplayedAttributes: displayedAttributes
      });
    } else {
      this.setState({
        productState: newProductState,
        initialDisplayedAttributes: {}
      });
    }
  };

  formProductPriceContractsInfo = (to, from, toPreview, fromPreview) => {
    let toInfo = cloneDeep(to);
    let fromInfo = cloneDeep(from);
    toInfo['priceModel'] = {};
    fromInfo['priceModel'] = {};

    toInfo.priceModel['id'] = toPreview.priceModelId;
    toInfo.priceModel['revisionId'] = toPreview.revisionId;
    toInfo.title = toPreview.title;

    fromInfo.priceModel['id'] = fromPreview.priceModelId;
    fromInfo.priceModel['revisionId'] = fromPreview.revisionId;
    fromInfo.title = fromPreview.title;

    const productPrice = [];
    [toInfo, fromInfo].forEach(termRevision => {
      productPrice.push({
        buyer: {
          buyerType: get(termRevision, 'buyer.type'),
          buyerId: get(termRevision, 'buyer.id')
        },
        seller: {
          sellerType: get(termRevision, 'seller.type'),
          sellerId: get(termRevision, 'seller.id')
        },
        priceModelId: get(termRevision, 'priceModel.id'),
        revisionId: get(termRevision, 'priceModel.revisionId'),
        effectiveStartDate: get(termRevision, 'effectiveStartDate', null),
        contractId: get(termRevision, 'termId'),
        contractRevisionId: get(termRevision, 'revisionId'),
        modifiers: {
          discountPercent: get(termRevision, 'modifiers.discountPercent', 0)
        },
        title: get(termRevision, 'title')
      });
    });
    return { productPrice };
  };

  render() {
    // TODO: This component does not support shipping contracts currently. When our backend starts supporting shipping contract patches, we should update this component to support.
    // Prerequisite of this change is supporting multiple Shipping Previews from the PricingGridWrapper component
    const { from, to, sku, fromPriceModelInfo, toPriceModelInfo, skuRuleSet, requiredInputs } = this.props;
    const { productState } = this.state;

    let skuData = {};
    if (productState && productState[sku] && productState[sku].selectedRow) {
      skuData = {};
      skuData[sku] = cloneDeep(productState[sku]) || {}; // use deep copy
      skuData[sku] = {
        ...skuData[sku],
        sellerAccountId: to.seller.accountId,
        sellerId: to.seller.id
      };
    }
    const { preview: fromPreview, hasMultipleCurrencies: fromHasMultipleCurrencies } = this.getGridData(
      from,
      fromPriceModelInfo,
      `Revision ${from.revisionId} | ${get(from, 'displayAgreementType') || 'Contract Price'}`
    );

    const { preview: toPreview, hasMultipleCurrencies: toHasMultipleCurrencies } = this.getGridData(
      to,
      toPriceModelInfo,
      `Revision ${to.revisionId} | ${get(to, 'displayAgreementType') || 'Contract Price'}`
    );

    const productPriceContractsInfo = this.formProductPriceContractsInfo(to, from, toPreview, fromPreview);
    let multipleCurrencyMsg;

    if (fromHasMultipleCurrencies || toHasMultipleCurrencies) {
      const multipleCurrencyTooltipMsg = (
        <div>
          Use the attribute selector below to change the destination country and see the different currency/prices.
        </div>
      );

      multipleCurrencyMsg = (fromHasMultipleCurrencies || toHasMultipleCurrencies) && (
        <Tooltip contents={multipleCurrencyTooltipMsg}>
          <p>
            <small>
              <code>
                * The pricing and currency for this product differs by destination country{' '}
                <i className="fa fa-question-circle" />
              </code>
            </small>
          </p>
        </Tooltip>
      );
    }
    if (!skuRuleSet) return null;
    return (
      <div>
        <div className={`col-xs-12 col-md-5`}>
          <UnionSelector
            productId={sku}
            showErrorModal
            authToken={auth.getAccessToken()}
            consumerId="price"
            showPivotConfig={false}
            onChange={this.onChange}
            showCompetitivePricing={false}
            competitorProducts={[]}
            onError={this.onError}
            requiredAttributes={requiredInputs[sku]}
          />
        </div>
        <div className={`col-xs-12 col-md-7`}>
          {multipleCurrencyMsg}
          <PricingGrid
            skuData={skuData}
            onGetAccessToken={auth.getAccessToken}
            isProduction={isProduction}
            parentService="contracts"
            widthOfGrid="100%"
            productPriceContractsInfo={productPriceContractsInfo.productPrice}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    requiredInputs: termSelectors.getTermItemsRequiredInputs(state)
  };
};

export default connect(mapStateToProps)(DiffPricingGrid);
